import type {
  Cell,
  ColumnDef,
  Row,
  Table,
  Header,
} from '@tanstack/react-table';
import React, { useCallback, useMemo, useRef } from 'react';
import type { ValueOf } from 'type-fest';

import { BOMRow, TableRow } from '@frontend/api';
import { Box, Center, Td, Th } from '@frontend/components';

import { TABLE_TYPES } from 'modules/extractedTables/constants';
import {
  SpreadsheetSelectionCell,
  SpreadsheetSelectionHeaderCellSelection,
  SpreadsheetSelectionHeaderCellType,
  UseSpreadsheetSelectionReturns,
  buildVisibleRowNumbers,
} from 'modules/quotes/quoteItemComponents/bomBuilder/useSpreadsheetSelection';
import { isSameCell } from 'modules/quotes/quoteItemComponents/bomBuilder/useSpreadsheetSelection/utils';

const CELL_OUTLINE_STYLES = {
  outlineWidth: 2,
  outlineOffset: -2,
  outlineStyle: 'solid',
  outlineColor: 'interaction.main',
  borderRadius: 'sm',
} as const;

const SELECTED_HEADER_STYLE = {
  backgroundColor: 'interaction.active',
  color: 'feature.onInteraction',
};

type TableMeta = {
  tableType?: ValueOf<typeof TABLE_TYPES>;
  bomRowIdxsByRowId?: Record<string, number>;
  headerCellOnClick?: (
    type: SpreadsheetSelectionHeaderCellType,
    index: number,
    event: React.MouseEvent<HTMLElement>,
    visibleRowNumbers: number[],
  ) => void;
  bodyCellOnMouseEnter?: (
    selectedCell: { row: number; column: number },
    event: React.MouseEvent<HTMLElement>,
  ) => void;
  bodyCellOnInteract?: (
    selectedCell: { row: number; column: number },
    isShiftPressed?: boolean,
  ) => void;
};

export const getTdId = (
  cell: Cell<TableRow, unknown> | Cell<BOMRow, unknown>,
) =>
  `ExtractedTable-${(String(cell.id) ?? '')
    .replaceAll(' ', '')
    .replaceAll('.', '-')}`;

export const getThId = (
  header: Header<TableRow, unknown> | Header<BOMRow, unknown>,
) => `ExtractedTableHeader-${(String(header.id) ?? '').replaceAll(' ', '')}`;

/**
 * Builds CSS styles for selected cells in a spreadsheet.
 *
 * @param rows - The rows in the spreadsheet.
 * @param selectedSpreadsheetCell - The selected spreadsheet cell.
 * @param extentSpreadsheetCell - The extent spreadsheet cell.
 * @returns An object containing CSS styles for the selected cells.
 */
export const buildCssForSelectedCells = (
  rows: Row<TableRow>[],
  selectedSpreadsheetCell: SpreadsheetSelectionCell | null,
  extentSpreadsheetCell: SpreadsheetSelectionCell | null,
  tableType: ValueOf<typeof TABLE_TYPES>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  // If there's just a single selected spreadsheet cell, style it accordingly
  if (selectedSpreadsheetCell) {
    const row = rows[selectedSpreadsheetCell.row];
    const cells = row?.getAllCells() ?? [];
    const cell = cells?.[selectedSpreadsheetCell.column];
    if (cell) {
      const cssSelector = `#${getTdId(cell)} > div`;
      result[cssSelector] = {
        ...CELL_OUTLINE_STYLES,
      };
    }
  }

  // If there's both a single cell selected, and an extent selected, we also want to highlight multiple cells, and even highlight the row "header"
  if (selectedSpreadsheetCell && extentSpreadsheetCell) {
    // Grab the indices where the extent of the selected rows/columns start and end
    const [startRow, endRow] = [
      selectedSpreadsheetCell.row,
      extentSpreadsheetCell.row,
    ].sort((a, b) => a - b);
    const [startCol, endCol] = [
      selectedSpreadsheetCell.column,
      extentSpreadsheetCell.column,
    ].sort((a, b) => a - b);

    // Iterate over the rows and columns within the extent of the selection and style them according to their selection
    for (let i = startRow; i <= endRow; i++) {
      const row = rows[i];
      const cells = row?.getAllCells() ?? [];

      for (let j = startCol; j <= endCol; j++) {
        const cell = cells[j];
        if (cell) {
          const cssSelector = `#${getTdId(cell)} > div`;
          const spreadSheetCell = { row: i, column: j };

          if (isSameCell(selectedSpreadsheetCell, spreadSheetCell)) {
            result[cssSelector] = {
              ...CELL_OUTLINE_STYLES,
              backgroundColor: 'interaction.overlayHover',
            };
          } else {
            result[cssSelector] = {
              backgroundColor: 'interaction.overlayHover',
            };
          }
        }
      }

      // If we're in a BOM table, and we're selecting the entire row, apply the selected header style to the first column (itemNumber)
      // NB: we can't select the first column in a BOM table, so we only highlight itemNumber if the entire row is selected,
      // that's the reason for this ugly logic/math
      if (
        tableType === TABLE_TYPES.BOM &&
        startCol === 1 &&
        endCol === cells.length - 1
      ) {
        const cssSelector = `#${getTdId(cells[0])} > div`;
        result[cssSelector] = SELECTED_HEADER_STYLE;
      }
    }
  }

  return result;
};

export const buildCssForHeaderCells = (
  headers: Header<TableRow, unknown>[],
  getRowOrHeaderCellState: UseSpreadsheetSelectionReturns['headerCellSelectedCells'],
  visibleRowNumbers: number[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const header of headers) {
    const selectionState = getRowOrHeaderCellState(
      SpreadsheetSelectionHeaderCellType.COLUMN,
      header.index,
      visibleRowNumbers,
    );
    const cssSelector = `#${getThId(header)}`;
    switch (selectionState) {
      case SpreadsheetSelectionHeaderCellSelection.ALL:
        result[cssSelector] = SELECTED_HEADER_STYLE;
        break;
      case SpreadsheetSelectionHeaderCellSelection.SOME:
      default:
        result[cssSelector] = {
          backgroundColor: 'page.plateLight',
        };
        break;
    }
  }

  return result;
};

type HeaderCellRenderProps = {
  headerName: string;
  table: Table<TableRow> | Table<BOMRow>;
  header: Header<TableRow, unknown> | Header<BOMRow, unknown>;
};

export const HeaderCellRender = ({
  headerName,
  table,
  header,
}: HeaderCellRenderProps) => {
  // TODO: try to figure out a better way to plumb in this action, since we are
  //  redundantly recalculating this when we already calculate it at the top level
  //  but cannot pass it in as table meta because its dependent on the table - maybe
  //  move it to an on key down handler at the grid level? Similar to how we manage
  //  cell state
  const { rows } = table.getRowModel();

  const visibleRowNumbers = useMemo(() => buildVisibleRowNumbers(rows), [rows]);

  const onHeaderCellClick: React.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      (table.options.meta as TableMeta)?.headerCellOnClick?.(
        SpreadsheetSelectionHeaderCellType.COLUMN,
        header.index,
        e,
        visibleRowNumbers,
      );
    },
    [header.index, table.options.meta, visibleRowNumbers],
  );

  return (
    <Th
      id={getThId(header)}
      userSelect="none"
      onClick={onHeaderCellClick}
      sx={{
        textWrap: 'nowrap',
      }}
      // If we're not a BOM, we want to redact headers
      data-fs-hide={
        (table.options.meta as TableMeta)?.tableType === TABLE_TYPES.RAW
          ? true
          : undefined
      }
    >
      {headerName}
    </Th>
  );
};

export const ItemNoHeaderCellRender = ({
  headerName,
  header,
  table,
}: HeaderCellRenderProps) => {
  return (
    <Th
      id={getThId(header)}
      userSelect="none"
      sx={{
        textWrap: 'nowrap',
      }}
      position="sticky"
      left={0}
      // If we're not a BOM, we want to redact the item number header specifically
      data-fs-hide={
        (table.options.meta as TableMeta)?.tableType === TABLE_TYPES.RAW
          ? true
          : undefined
      }
    >
      {headerName}
    </Th>
  );
};

function convertVersionString(version: string): string {
  // Split the version string by '.' and convert each part to a number
  let parts = version.split('.').map(Number);

  if (parts.length > 1) {
    // drop the first zero
    if (parts[0] === 0) {
      parts = parts.slice(1, parts.length);
    }
  }

  // since indexing starts at zero but we want it to start at one, add one
  parts = parts.map((num) => num + 1);

  // Join parts back into a string
  return parts.join('.');
}

export const ItemNoCellRender = ({
  row,
  cell,
  table,
}: {
  table: Table<TableRow> | Table<BOMRow>;
  row: Row<BOMRow>;
  cell: Cell<BOMRow, unknown>;
}) => {
  // We need to determine the "actual" row id and row index (as they are used under the hood) for selection behavior and to offset the shown Id
  const actualRowId =
    row.id === '0' && (row.subRows?.length ?? 0) > 0
      ? 'Root'
      : convertVersionString(row.id);

  // Calculate the actual row index based on the parent id.
  const actualRowIdx =
    (table.options.meta as TableMeta)?.bomRowIdxsByRowId?.[actualRowId] ??
    row.index;

  const { rows } = table.getRowModel();

  const visibleRowNumbers = useMemo(() => buildVisibleRowNumbers(rows), [rows]);

  // NB: headerCellOnClick is a misnomer, it also handles row selection
  const onItemNumberCellClick: React.MouseEventHandler<HTMLElement> =
    useCallback(
      (e) => {
        (table.options.meta as TableMeta)?.headerCellOnClick?.(
          SpreadsheetSelectionHeaderCellType.ROW,
          actualRowIdx,
          e,
          visibleRowNumbers,
        );
      },
      [actualRowIdx, table.options.meta, visibleRowNumbers],
    );

  return (
    <Td
      id={getTdId(cell)}
      scope="row"
      className="group"
      fontFamily="data"
      color="page.plateDark"
      background="page.plateLight"
      _focus={{ outline: 'none' }}
      _focusVisible={{
        boxShadow: 'focusInset',
      }}
      paddingLeft={1}
      sx={{
        width: `100px`,
      }}
      userSelect="none"
      onClick={onItemNumberCellClick}
      position="sticky"
      left={0}
    >
      <>
        <Box as="span">{actualRowId}</Box>
      </>
    </Td>
  );
};

const PartNumberContainer = ({
  cell,
  children,
}: React.PropsWithChildren<{
  cell: Cell<BOMRow, unknown>;
}>) => (
  <>
    {cell.row.index !== 0 || cell.row.parentId ? (
      <Box paddingLeft={cell.row.depth} />
    ) : null}
    {children}
  </>
);

function CellContainer({
  cell,
  children,
}: React.PropsWithChildren<{
  cell: Cell<BOMRow, unknown> | Cell<TableRow, unknown>;
}>) {
  switch (cell.column.id) {
    case 'partNumber':
      return (
        <PartNumberContainer cell={cell as Cell<BOMRow, unknown>}>
          {children}
        </PartNumberContainer>
      );
    case 'revision':
    case 'count':
      return <Center>{children}</Center>;
    default:
      return <>{children}</>;
  }
}

export const defaultColumn: Partial<ColumnDef<TableRow, string>> = {
  cell: function Cell({ table, cell }) {
    const { rows } = table.getRowModel();
    const spreadSheetSelectionCell = useMemo(
      () => ({
        row: rows.findIndex((r) => r.id === cell.row.id),
        column: cell.column.getIndex(),
      }),
      [rows, cell.row.id, cell.column],
    );
    const cellRef = useRef<HTMLTableCellElement>(null);
    const innerCellRef = useRef<HTMLDivElement>(null);

    const getIsCellFocused = useCallback(
      () => document.activeElement === cellRef.current,
      [],
    );

    const onCellMouseEnter: React.MouseEventHandler<HTMLElement> = useCallback(
      (event) =>
        (table.options.meta as TableMeta)?.bodyCellOnMouseEnter?.(
          spreadSheetSelectionCell,
          event,
        ),
      [table.options.meta, spreadSheetSelectionCell],
    );
    const onCellMouseDown: React.MouseEventHandler<HTMLElement> = useCallback(
      (e) => {
        if (getIsCellFocused()) {
          e.preventDefault();
        } else {
          (table.options.meta as TableMeta)?.bodyCellOnInteract?.(
            spreadSheetSelectionCell,
            e.shiftKey,
          );
        }
      },
      [getIsCellFocused, table.options.meta, spreadSheetSelectionCell],
    );

    const cellInnerProps = useMemo(
      () => ({
        flexDirection: 'row',
        alignItems: 'center',
        color: 'feature.primary',

        ...cell.column.columnDef.meta?.getCellInnerProps?.(cell),
        onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          onCellMouseDown(e);
        },
        className: 'group',
      }),
      [cell, onCellMouseDown],
    );

    const cellValue = cell.getValue() ?? '';

    return (
      <Td
        id={getTdId(cell)}
        ref={cellRef}
        paddingY={0.5}
        userSelect="none"
        innerRef={innerCellRef}
        height={4}
        onMouseEnter={onCellMouseEnter}
        innerProps={cellInnerProps}
        data-fs-hide={cellValue !== '' ? true : undefined}
      >
        <CellContainer cell={cell}>
          <Box as="span" flex={1}>
            {cellValue}
          </Box>
        </CellContainer>
      </Td>
    );
  },
};
